import sortBy from 'lodash/sortBy';
import React, { useEffect, useMemo, useState } from 'react';

import { changeImpersonation } from '@tempus/t-shared/ui';
import { Button } from '@tcl-boron-prefabs/button';
import { DropdownOption } from '@tcl-models/models';
import { GrayscalePalette } from '@tcl-boron-colors/colors';
import { Modal } from '@tcl-boron-prefabs/modal';
import { Banner } from '@tcl-boron-prefabs/banner';
import { Warning } from '@tcl-boron-icons/icons';
import { SingleSelectComboBox } from '@tcl-boron-prefabs/single-select-combo-box';

import { useDispatch, useSelector } from '~/store';
import { impersonatedIds } from '~/store/api/axios';
import { creators as siteCreators } from '~/store/site';
import { Institution } from '~/store/site/types';

import { getPathForImpersonation } from '~/components/_core/Navigation/Navigation.helpers';
import { getTrialsLandingPath } from '~/utils/toggleHelpers';
import { createUseStyles } from 'react-jss';

const getCurrentImpersonation = (timeSites: Institution[]) => {
  const id = impersonatedIds[0];
  const site = timeSites.find((site) => site.id === id);

  if (!id) {
    return null;
  } else if (!site) {
    return {
      value: id,
      label: `[Site Not Found | ID = ${id}]`,
    };
  }
  return {
    value: site.id,
    label: site.shortName || site.name,
  };
};

const useStyles = createUseStyles({
  host: {
    width: '100%',
  },
  bar: {
    backgroundColor: '#C7DDF6',
    width: '100%',
  },
  banner: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'space-between',
  },
});

const ImpersonationBanner: React.FC = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [selectedSite, setSelectedSite] = useState<DropdownOption | null>(null);

  const canImpersonate = useSelector(({ user }) => user.canImpersonate);
  const impersonationModalOpen = useSelector(({ site }) => site.impersonationModalOpen);
  const timeSites = useSelector(({ site }) => site.timeSites);
  const userLoaded = useSelector(({ user }) => user.loaded);

  const currentImpersonation = useMemo(() => {
    return getCurrentImpersonation(timeSites);
  }, [timeSites]);

  const siteOptions = useMemo(() => {
    return sortBy(timeSites, (a) => a.shortName || a.name).map((site) => ({
      label: site.shortName || site.name,
      value: site.id,
    }));
  }, [timeSites]);

  useEffect(() => {
    if (userLoaded && canImpersonate) {
      dispatch(siteCreators.getAllSites());
    }
  }, [dispatch, userLoaded, canImpersonate]);

  useEffect(() => {
    setSelectedSite(currentImpersonation);
  }, [currentImpersonation]);

  const onClose = () => {
    dispatch(siteCreators.setImpersonationModalOpen(false));
  };

  const clearImpersonation = () => {
    changeImpersonation('', (v) => getPathForImpersonation(document.location.pathname, v));
  };

  const reloadWithImpersonation = () => {
    changeImpersonation(selectedSite?.value || '', () => getTrialsLandingPath());
  };

  return (
    <section className={classes.host}>
      {currentImpersonation && (
        <div className={classes.bar}>
          <Banner
            ariaLabel="Impersonation Banner"
            classes={{ content: classes.banner }}
            status="default"
            style={{
              backgroundColor: 'transparent',
              borderRadius: 0,
              boxShadow: 'none',
              margin: '0 auto',
              maxWidth: 1216,
              width: '100%',
            }}
            Icon={() => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Warning />
                  <b>Preview Mode</b>
                </div>
                <span>
                  You are currently viewing as <b>{currentImpersonation?.label}</b>.
                </span>
              </div>
            )}
            action={
              <Button ariaLabel="Exit impersonation mode" onClick={clearImpersonation} small>
                Exit preview mode
              </Button>
            }
          />
        </div>
      )}
      <Modal
        size="medium"
        isOpen={impersonationModalOpen}
        title="View Therapies Portal as a specific site"
        style={{
          overlay: { zIndex: 5 },
          content: { background: `${GrayscalePalette[100]}` },
        }}
        callToActionButton={{
          id: 'confirm',
          buttonText: 'Confirm',
          onClick: reloadWithImpersonation,
        }}
        cancelButton={{
          id: 'cancel',
          buttonText: 'Cancel',
          onClick: onClose,
        }}
      >
        <SingleSelectComboBox
          aria-label="Select a site to impersonate"
          clearable
          escapeClippingArea
          label="Site"
          onChange={setSelectedSite}
          style={{ overflow: 'auto', paddingBottom: '4px' }}
          value={selectedSite}
          options={siteOptions}
        />
      </Modal>
    </section>
  );
};

export default ImpersonationBanner;
